import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'how-to-request-your-home-office-immigration-records';

export const RequestImmigrationRecords = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        If you came into care as an unaccompanied asylum-seeking child, your history sits in two
        separate places, held by two different organisations, under two different sets of rules.
        The council that looked after you holds an ordinary care file, no different in law from
        any other looked-after child&apos;s. The Home Office holds a completely separate
        immigration and asylum file — and that one comes with an extra complication the council
        side does not have. This guide covers both, and is honest about where the Home Office side
        gets genuinely harder.
      </p>
    </GuideSection>

    <GuideSection eyebrow="The reassuring part first" title="Your council file works exactly like any other care leaver's">
      <p>
        Being a UASC does not change your rights against the local authority that looked after
        you. There is no special exemption that applies to a council&apos;s own social work,
        placement or education records just because you arrived as an asylum seeker — request
        exactly as our{' '}
        <Link to="/guides/how-to-request-your-care-records-from-the-council" className="text-brand underline hover:opacity-90">
          guide to requesting care records from the council
        </Link>{' '}
        describes. The extra difficulty below is specific to the Home Office side.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Requesting the Home Office file" title="How to make the request">
      <p>
        The Home Office handles this centrally, through its{' '}
        <strong className="text-ink">Subject Access Request Unit (SARU)</strong>, covering
        immigration, asylum and Border Force data in one system — there is no separate route
        specifically for asylum casework. The main options:
      </p>
      <GuideChecklist
        items={[
          <>
            An{' '}
            <SourceLink href="https://visas-immigration.service.gov.uk/product/saru">
              online application
            </SourceLink>{' '}
            — the primary route.
          </>,
          'By email, to subjectaccessrequest@homeoffice.gov.uk, if the online form does not work for your situation.',
          'By post, to the Subject Access Request Unit, 2 Ruskin Square, Dingwall Road, Croydon, CR0 2WF.',
        ]}
      />
      <p>
        There is no fee. The form lets you choose how detailed a response you want — choose the
        fullest option if you want caseworker notes, interview records, decision letters and
        correspondence, not just a summary.
      </p>
      <GuideNote>
        <p>
          <strong>A SAR is not how you prove your current status.</strong> The Home Office is
          explicit that you cannot use a Subject Access Request to get an immigration status
          document — if what you actually need is proof of your current status (for a job, a
          landlord, a bank), use the separate{' '}
          <SourceLink href="https://www.gov.uk/view-prove-immigration-status">
            view and prove your immigration status
          </SourceLink>{' '}
          service instead. A SAR is for understanding your history, not evidencing your present
          status.
        </p>
      </GuideNote>
    </GuideSection>

    <GuideSection eyebrow="Where it gets harder" title="The immigration exemption">
      <p>
        The Home Office — and only the Home Office, no other organisation can use this — can
        withhold information from a SAR under a specific exemption in{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2018/12/schedule/2/paragraph/4">
          Schedule 2, paragraph 4 of the Data Protection Act 2018
        </SourceLink>
        , where giving you full access would be likely to prejudice the maintenance of effective
        immigration control, or the investigation of activity that would undermine it.
      </p>
      <p>
        This has a genuinely troubled history, which matters for how confidently the Home Office
        can actually rely on it today. Campaigners{' '}
        <SourceLink href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/exemptions/immigration-exemption-a-guide/">
          successfully challenged
        </SourceLink>{' '}
        the exemption in the Court of Appeal in 2021 for lacking proper safeguards — and when the
        government&apos;s first attempt at fixing it still was not good enough, the Court of Appeal
        struck that down too, in December 2023. The version in force now, since March 2024, is the
        result of losing that fight twice. The Home Office must now show, case by case, that
        applying the exemption is genuinely necessary and proportionate, that it has weighed the
        risk to you against the immigration-control justification, and it must specifically
        consider vulnerability — which, for someone whose file concerns them as an unaccompanied
        child, is directly relevant. It cannot simply withhold your file wholesale as a blanket
        policy.
      </p>
    </GuideSection>

    <GuideCTA />

    <GuideSection eyebrow="A specific complication" title="Age assessment records">
      <p>
        If your age was ever formally assessed or disputed, those records can sit in more than one
        place. An age assessment can be carried out by the local authority itself, or referred to
        the{' '}
        <strong className="text-ink">National Age Assessment Board</strong>, a Home Office unit
        that conducts assessments on referral from a council. It is worth asking both the council
        and the Home Office specifically about age assessment records, rather than assuming one
        request will surface everything — and being aware that the Home Office side of an age
        assessment record could, in principle, fall within the same immigration exemption
        described above.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Getting help" title="Organisations that know this territory well">
      <GuideChecklist
        items={[
          <>
            <strong className="text-ink">Coram Children&apos;s Legal Centre</strong> — its
            Migrant Children&apos;s Project and Legal Practice Unit specifically cover the
            immigration claims of looked-after children and care leavers, not just general
            immigration advice.
          </>,
          <>
            <strong className="text-ink">Refugee Council</strong> — its Independent Unaccompanied
            Asylum-Seeking Children Support Service (IUSS) helps under-18s with the asylum process
            and family tracing, though it is explicit that it cannot help with age disputes or act
            as an appropriate adult for an age assessment specifically.
          </>,
          <>
            <strong className="text-ink">British Red Cross</strong> — its International Family
            Tracing service is free and confidential, for anyone who has lost contact with family
            through conflict, migration or disaster.
          </>,
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="immigration-records-faq">
        <GuideFaqItem
          q="Can I make this request myself if I'm still under 18?"
          a="Official guidance is clear that anyone applying on behalf of a child under 12 needs to show parental responsibility, but it doesn't clearly address a teenager applying for their own record. If you're a younger unaccompanied minor, it's worth getting help from Coram Children's Legal Centre or the Refugee Council's IUSS service rather than navigating this alone."
        />
        <GuideFaqItem
          q="Will the Home Office tell me if it withheld something under the immigration exemption?"
          a="Under the safeguards added after the exemption was successfully challenged in court, the Home Office is generally expected to tell you when it has relied on the exemption — it isn't meant to simply hand back a redacted file with no explanation. If that happens without any explanation, it's worth challenging."
        />
        <GuideFaqItem
          q="Does my council file get affected by anything in my immigration file?"
          a="No — they're legally separate, held by separate controllers, and the immigration exemption only ever applies to the Home Office's own data. Whatever the council holds about your time in its care is accessed through the ordinary council SAR route, with no immigration-specific restriction."
        />
        <GuideFaqItem
          q="What if I don't know my Home Office reference number?"
          a="Give whatever you do know — your name (including any spelling variations used at the time), date of birth, and roughly when you claimed asylum. The Home Office can search on those details; you don't need the reference number to start."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        The Home Office&apos;s own process is set out on{' '}
        <SourceLink href="https://www.gov.uk/government/publications/requests-for-personal-data-uk-visas-and-immigration">
          gov.uk
        </SourceLink>
        . The immigration exemption is in{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2018/12/schedule/2/paragraph/4">
          Schedule 2, paragraph 4 of the Data Protection Act 2018
        </SourceLink>
        , explained by the ICO&apos;s{' '}
        <SourceLink href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/exemptions/immigration-exemption-a-guide/">
          immigration exemption guide
        </SourceLink>
        . The National Age Assessment Board was established under sections 50 and 51 of the
        Nationality and Borders Act 2022.
      </p>
    </GuideSection>

    <GuideCTA
      heading="We keep your council-side request organised"
      body="Precious Data drafts and tracks your Subject Access Request to the local authority that looked after you, alongside every other organisation from your time in care — the Home Office side runs through its own separate process, but nothing on the council side gets lost."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default RequestImmigrationRecords;
